/**
 * src/shell/agents/components/GStackAgentsHubModal.tsx
 *
 * Unified Multi-Agent Autonomous Hub Console for Sovereign Mesh:
 * - Landing Page Generator (GTM / Marketing)
 * - Weekly Auto-Retro Reporter (Ops / Engineering)
 * - Competitor Battle-Card Generator (Sales / Strategy)
 * - Real-Time Job Progress, Sandboxed Previews & Cost Meter
 */

import React, { useState, useEffect } from 'react';
import { landingPageAgentEngine } from '../services/LandingPageAgentEngine';
import { weeklyRetroAgentEngine } from '../services/WeeklyRetroAgentEngine';
import { battleCardAgentEngine } from '../services/BattleCardAgentEngine';
import { agentJobQueue } from '../services/AgentJobQueue';
import { agentCostController } from '../services/AgentCostController';
import { agentFeedbackLoop } from '../services/AgentFeedbackLoop';
import {
  LandingPageResult,
  WeeklyRetroResult,
  BattleCardResult,
  AgentJob,
} from '../types/gstack-agents-types';
import { LandingPagePreview } from './LandingPagePreview';
import { RetroDashboard } from './RetroDashboard';
import { BattleCardMatrix } from './BattleCardMatrix';

interface GStackAgentsHubModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  tenantId?: string;
}

export const GStackAgentsHubModal: React.FC<GStackAgentsHubModalProps> = ({
  isOpen,
  onClose,
  userId = 'admin_1',
  tenantId = 'tenant_mesh_prime',
}) => {
  const [activeTab, setActiveTab] = useState<'landing' | 'retro' | 'battlecard' | 'jobs'>('landing');

  // Landing Page Form State
  const [productName, setProductName] = useState('Sovereign Mesh Platform');
  const [oneLiner, setOneLiner] = useState('Decentralized Edge Intelligence & Peer-to-Peer Communication');
  const [description, setDescription] = useState(
    'A high-performance microkernel mesh running offline-first communication with cryptographic non-repudiation.'
  );
  const [pains, setPains] = useState('Centralized vendor lock-in, recurring SaaS markups, single point of failure');
  const [benefits, setBenefits] = useState('Zero-knowledge privacy, BLE/WebRTC peer routing, hardware-enforced memory safety');
  const [ctaPrimary, setCtaPrimary] = useState('Deploy Sovereign Node');
  const [landingVariantsCount, setLandingVariantsCount] = useState(3);

  // Retro Form State
  const [retroPeriodFrom, setRetroPeriodFrom] = useState('2026-08-25');
  const [retroPeriodTo, setRetroPeriodTo] = useState('2026-08-31');

  // Battle Card Form State
  const [competitorName, setCompetitorName] = useState('Slack / Discord');
  const [competitorUrl, setCompetitorUrl] = useState('https://slack.com');

  // Agent State & Results
  const [currentJob, setCurrentJob] = useState<AgentJob | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [landingResult, setLandingResult] = useState<LandingPageResult | null>(null);
  const [retroResult, setRetroResult] = useState<WeeklyRetroResult | null>(null);
  const [battleResult, setBattleResult] = useState<BattleCardResult | null>(null);

  const [rating, setRating] = useState<number>(5);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [budget, setBudget] = useState(() => agentCostController.getBudget(userId));

  useEffect(() => {
    setBudget(agentCostController.getBudget(userId));
  }, [userId, isExecuting]);

  if (!isOpen) return null;

  const runLandingAgent = async () => {
    setIsExecuting(true);
    setErrorMsg(null);
    setProgressPct(5);
    setStatusMsg('Initializing GTM Landing Page Synthesizer...');

    const job = agentJobQueue.enqueueJob('gtm.landing_page_gen', userId, tenantId, { productName });
    setCurrentJob(job);

    try {
      const result = await landingPageAgentEngine.generateLandingPages(
        {
          tenantId,
          requestedBy: userId,
          role: 'superadmin',
          product: {
            name: productName,
            oneLiner,
            description,
            targetUsers: ['CTOs', 'Security Architects', 'Decentralized Builders'],
            pains: pains.split(',').map((s) => s.trim()),
            benefits: benefits.split(',').map((s) => s.trim()),
            ctaPrimary,
          },
          variantsCount: landingVariantsCount,
        },
        (prog, msg) => {
          setProgressPct(prog);
          setStatusMsg(msg);
          agentJobQueue.updateProgress(job.id, prog, msg);
        }
      );

      setLandingResult(result);
      agentJobQueue.completeJob(job.id, result, result.audit.estimatedCostUsd, result.audit.tokensUsed);
    } catch (err: any) {
      setErrorMsg(err.message || 'Execution failed');
      agentJobQueue.failJob(job.id, err.message || 'Execution failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const runRetroAgent = async () => {
    setIsExecuting(true);
    setErrorMsg(null);
    setProgressPct(5);
    setStatusMsg('Initializing Weekly Retro Reporter...');

    const job = agentJobQueue.enqueueJob('ops.weekly_retro', userId, tenantId, { retroPeriodFrom, retroPeriodTo });
    setCurrentJob(job);

    try {
      const result = await weeklyRetroAgentEngine.generateRetro(
        {
          tenantId,
          requestedBy: userId,
          role: 'superadmin',
          period: { from: retroPeriodFrom, to: retroPeriodTo },
        },
        (prog, msg) => {
          setProgressPct(prog);
          setStatusMsg(msg);
          agentJobQueue.updateProgress(job.id, prog, msg);
        }
      );

      setRetroResult(result);
      agentJobQueue.completeJob(job.id, result, result.audit.estimatedCostUsd, result.audit.tokensUsed);
    } catch (err: any) {
      setErrorMsg(err.message || 'Execution failed');
      agentJobQueue.failJob(job.id, err.message || 'Execution failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const runBattleCardAgent = async () => {
    setIsExecuting(true);
    setErrorMsg(null);
    setProgressPct(5);
    setStatusMsg('Synthesizing Competitor Battle Cards...');

    const job = agentJobQueue.enqueueJob('sales.battle_card_gen', userId, tenantId, { competitorName });
    setCurrentJob(job);

    try {
      const result = await battleCardAgentEngine.generateBattleCards(
        {
          tenantId,
          requestedBy: userId,
          role: 'superadmin',
          competitors: [{ name: competitorName, url: competitorUrl }],
          ourProduct: {
            name: productName,
            oneLiner,
            keyDifferentiators: [
              'Zero-knowledge peer-to-peer routing with sub-10ms latency',
              'Offline-first synchronization over local mesh and BLE',
              'Hardware-enforced memory isolation and Ed25519 audit proofs',
            ],
          },
        },
        (prog, msg) => {
          setProgressPct(prog);
          setStatusMsg(msg);
          agentJobQueue.updateProgress(job.id, prog, msg);
        }
      );

      setBattleResult(result);
      agentJobQueue.completeJob(job.id, result, result.audit.estimatedCostUsd, result.audit.tokensUsed);
    } catch (err: any) {
      setErrorMsg(err.message || 'Execution failed');
      agentJobQueue.failJob(job.id, err.message || 'Execution failed');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleSendFeedback = () => {
    agentFeedbackLoop.recordFeedback(
      activeTab === 'landing' ? 'gtm.landing_page_gen' : activeTab === 'retro' ? 'ops.weekly_retro' : 'sales.battle_card_gen',
      userId,
      productName,
      'original_generated_output',
      'user_reviewed_output',
      rating
    );
    setFeedbackSuccess(true);
    setTimeout(() => setFeedbackSuccess(false), 3000);
  };

  return (
    <div
      data-testid="gstack-agents-hub-modal"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 110,
        background: 'rgba(10, 15, 29, 0.92)',
        backdropFilter: 'blur(16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1280px',
          height: '92vh',
          background: '#0b0f19',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: 16,
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            background: 'rgba(30, 41, 59, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24 }}>⚡</span>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: 0 }}>
                Autonomous Agents Hub
              </h2>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                High-Impact gstack Agents • Microkernel Orchestration • Ed25519 Signed Ledger
              </div>
            </div>
          </div>

          {/* Budget & Close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              data-testid="agent-budget-indicator"
              style={{
                background: 'rgba(0,0,0,0.4)',
                padding: '6px 12px',
                borderRadius: 8,
                fontSize: 12,
                color: '#cbd5e1',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <span>Daily Budget: </span>
              <strong style={{ color: '#4ade80' }}>
                ${budget.dailySpentUsd.toFixed(2)} / ${budget.dailyBudgetUsd.toFixed(2)}
              </strong>
              <span style={{ color: '#64748b', marginLeft: 6 }}>
                ({budget.runsToday}/{budget.maxRunsPerDay} runs)
              </span>
            </div>

            <button
              type="button"
              data-testid="btn-close-agents-hub"
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                border: 'none',
                color: '#94a3b8',
                fontSize: 16,
                padding: '6px 12px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '12px 24px',
            background: 'rgba(15, 23, 42, 0.6)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <button
            type="button"
            data-testid="tab-agent-landing"
            onClick={() => setActiveTab('landing')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              background: activeTab === 'landing' ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : 'transparent',
              color: activeTab === 'landing' ? '#fff' : '#94a3b8',
              border: 'none',
            }}
          >
            🎨 Landing Page Generator
          </button>
          <button
            type="button"
            data-testid="tab-agent-retro"
            onClick={() => setActiveTab('retro')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              background: activeTab === 'retro' ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
              color: activeTab === 'retro' ? '#fff' : '#94a3b8',
              border: 'none',
            }}
          >
            📊 Weekly Auto-Retro Reporter
          </button>
          <button
            type="button"
            data-testid="tab-agent-battlecard"
            onClick={() => setActiveTab('battlecard')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              background: activeTab === 'battlecard' ? 'linear-gradient(135deg, #e11d48, #be123c)' : 'transparent',
              color: activeTab === 'battlecard' ? '#fff' : '#94a3b8',
              border: 'none',
            }}
          >
            ⚔️ Competitor Battle Cards
          </button>
          <button
            type="button"
            data-testid="tab-agent-jobs"
            onClick={() => setActiveTab('jobs')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              background: activeTab === 'jobs' ? 'rgba(255,255,255,0.1)' : 'transparent',
              color: activeTab === 'jobs' ? '#fff' : '#94a3b8',
              border: 'none',
            }}
          >
            📋 Job Queue
          </button>
        </div>

        {/* Live Progress Bar */}
        {isExecuting && (
          <div
            data-testid="agent-progress-banner"
            style={{
              padding: '12px 24px',
              background: 'rgba(30, 41, 59, 0.8)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6, color: '#38bdf8' }}>
              <span>{statusMsg}</span>
              <span>{progressPct}%</span>
            </div>
            <div style={{ height: 4, background: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {errorMsg && (
          <div style={{ padding: '10px 24px', background: '#7f1d1d', color: '#fca5a5', fontSize: 13 }}>
            ⚠️ {errorMsg}
          </div>
        )}

        {/* Main Content Area */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left Form Panel */}
          {activeTab !== 'jobs' && (
            <div
              style={{
                width: '380px',
                borderRight: '1px solid rgba(255, 255, 255, 0.08)',
                padding: '20px 24px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                background: 'rgba(15, 23, 42, 0.4)',
              }}
            >
              {activeTab === 'landing' && (
                <>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa', margin: 0 }}>
                    Product Brief Configuration
                  </h3>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Product Name</label>
                    <input
                      type="text"
                      data-testid="input-landing-name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>One-Liner Hook</label>
                    <input
                      type="text"
                      data-testid="input-landing-oneliner"
                      value={oneLiner}
                      onChange={(e) => setOneLiner(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Core Value Proposition</label>
                    <textarea
                      rows={3}
                      data-testid="input-landing-desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Customer Pains (comma separated)</label>
                    <input
                      type="text"
                      data-testid="input-landing-pains"
                      value={pains}
                      onChange={(e) => setPains(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Key Benefits</label>
                    <input
                      type="text"
                      data-testid="input-landing-benefits"
                      value={benefits}
                      onChange={(e) => setBenefits(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Primary CTA Text</label>
                    <input
                      type="text"
                      data-testid="input-landing-cta"
                      value={ctaPrimary}
                      onChange={(e) => setCtaPrimary(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Variants to Synthesize: {landingVariantsCount}</label>
                    <input
                      type="range"
                      min={1}
                      max={4}
                      value={landingVariantsCount}
                      onChange={(e) => setLandingVariantsCount(Number(e.target.value))}
                      style={{ width: '100%', marginTop: 6 }}
                    />
                  </div>

                  <button
                    type="button"
                    data-testid="btn-generate-landing"
                    disabled={isExecuting}
                    onClick={runLandingAgent}
                    style={{
                      marginTop: 8,
                      padding: '12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 700,
                      border: 'none',
                      cursor: isExecuting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                    }}
                  >
                    {isExecuting ? 'Synthesizing...' : `🚀 Generate ${landingVariantsCount} Variants`}
                  </button>
                </>
              )}

              {activeTab === 'retro' && (
                <>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#34d399', margin: 0 }}>
                    Telemetry Period & Scope
                  </h3>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>From Date</label>
                    <input
                      type="date"
                      data-testid="input-retro-from"
                      value={retroPeriodFrom}
                      onChange={(e) => setRetroPeriodFrom(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>To Date</label>
                    <input
                      type="date"
                      data-testid="input-retro-to"
                      value={retroPeriodTo}
                      onChange={(e) => setRetroPeriodTo(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, background: 'rgba(0,0,0,0.2)', padding: 10, borderRadius: 6 }}>
                    🔒 Scoped telemetry will be scrubbed for all PII, emails, IP addresses, and tokens prior to LLM reasoning.
                  </div>

                  <button
                    type="button"
                    data-testid="btn-generate-retro"
                    disabled={isExecuting}
                    onClick={runRetroAgent}
                    style={{
                      marginTop: 12,
                      padding: '12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 700,
                      border: 'none',
                      cursor: isExecuting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    {isExecuting ? 'Compiling Retro...' : '📊 Compile Weekly Retro'}
                  </button>
                </>
              )}

              {activeTab === 'battlecard' && (
                <>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#f43f5e', margin: 0 }}>
                    Competitor Parameters
                  </h3>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Competitor Name</label>
                    <input
                      type="text"
                      data-testid="input-battle-comp-name"
                      value={competitorName}
                      onChange={(e) => setCompetitorName(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: '#94a3b8' }}>Public Product URL</label>
                    <input
                      type="text"
                      data-testid="input-battle-comp-url"
                      value={competitorUrl}
                      onChange={(e) => setCompetitorUrl(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, marginTop: 4 }}
                    />
                  </div>

                  <div style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(0,0,0,0.2)', padding: 10, borderRadius: 6 }}>
                    ⚖️ <strong>Compliance Disclaimer:</strong> Battle cards synthesize publicly available or user-provided parameters without unauthorized scraping.
                  </div>

                  <button
                    type="button"
                    data-testid="btn-generate-battlecard"
                    disabled={isExecuting}
                    onClick={runBattleCardAgent}
                    style={{
                      marginTop: 12,
                      padding: '12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #e11d48, #be123c)',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 700,
                      border: 'none',
                      cursor: isExecuting ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 14px rgba(225, 29, 72, 0.4)',
                    }}
                  >
                    {isExecuting ? 'Synthesizing Cards...' : '⚔️ Generate Battle Cards'}
                  </button>
                </>
              )}

              {/* Feedback Loop Box */}
              <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Quality Feedback Loop</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      data-testid={`btn-rate-star-${star}`}
                      onClick={() => setRating(star)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: 16,
                        cursor: 'pointer',
                        color: star <= rating ? '#fbbf24' : '#475569',
                      }}
                    >
                      ★
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="btn-submit-feedback"
                    onClick={handleSendFeedback}
                    style={{
                      marginLeft: 'auto',
                      padding: '4px 8px',
                      fontSize: 11,
                      background: 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    Submit
                  </button>
                </div>
                {feedbackSuccess && (
                  <div style={{ fontSize: 10, color: '#4ade80', marginTop: 4 }}>
                    ✓ Feedback recorded for model tuning
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Right Visual Result Panel */}
          <div style={{ flex: 1, padding: 24, overflowY: 'auto', background: '#020617' }}>
            {activeTab === 'landing' && (
              landingResult ? (
                <LandingPagePreview variants={landingResult.variants} productName={productName} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
                  <span style={{ fontSize: 48, marginBottom: 12 }}>🎨</span>
                  <p>Configure product brief on the left and click "Generate Variants" to view live landing pages.</p>
                </div>
              )
            )}

            {activeTab === 'retro' && (
              retroResult ? (
                <RetroDashboard result={retroResult} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
                  <span style={{ fontSize: 48, marginBottom: 12 }}>📊</span>
                  <p>Select date period and click "Compile Weekly Retro" to aggregate Git telemetry.</p>
                </div>
              )
            )}

            {activeTab === 'battlecard' && (
              battleResult ? (
                <BattleCardMatrix result={battleResult} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
                  <span style={{ fontSize: 48, marginBottom: 12 }}>⚔️</span>
                  <p>Enter competitor name and click "Generate Battle Cards" for competitive talk tracks.</p>
                </div>
              )
            )}

            {activeTab === 'jobs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>
                  Agent Execution History & Background Jobs
                </h3>
                {agentJobQueue.listJobs().map((j) => (
                  <div
                    key={j.id}
                    style={{
                      background: 'rgba(30, 41, 59, 0.6)',
                      padding: 16,
                      borderRadius: 8,
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: j.status === 'COMPLETED' ? '#065f46' : j.status === 'FAILED' ? '#7f1d1d' : '#1e3a8a',
                            color: j.status === 'COMPLETED' ? '#6ee7b7' : j.status === 'FAILED' ? '#fca5a5' : '#93c5fd',
                          }}
                        >
                          {j.status}
                        </span>
                        <strong style={{ color: '#fff', fontSize: 13 }}>{j.agentId}</strong>
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                        {j.statusMessage}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 11, color: '#64748b' }}>
                      <div>Tokens: {j.tokensUsed}</div>
                      <div>Cost: ${j.estimatedCostUsd.toFixed(4)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
